import { Column } from '../models/column.model';

/** The part of a column caption resolution needs. */
export type CaptionedColumn = { field?: string; headerText?: string };

/** Reads one translation key; `TranslateService.instant`. */
export type CaptionReader = (key: string) => string;

/** What a batched caption write needs of a DevExtreme grid. */
export type CaptionTarget = {
  beginUpdate: () => void;
  columnOption: (id: string, optionName: string, optionValue: unknown) => void;
  endUpdate: () => void;
};

/**
 * A column's caption, or the one it came with where there is no translation.
 * @param {CaptionedColumn} column The column being drawn.
 * @param {string} prefix Namespace the captions are keyed under.
 * @param {CaptionReader} read Reads one key.
 * @returns {string} The caption to show.
 */
export const resolveCaption = (
  column: CaptionedColumn,
  prefix: string,
  read: CaptionReader,
): string => {
  const field = String(column.field);

  // The default-field namespaces are keyed in lower case, module captions in
  // the field's own case — so both are tried.
  for (const key of [`${prefix}${field}`, `${prefix}${field.toLowerCase()}`]) {
    const caption = read(key);
    if (caption !== key) {
      return caption;
    }
  }

  return column.headerText ?? '';
};

/**
 * The caption for each field. Held against the column set, not the language —
 * a caption changing under the binding repaints the grid once per column.
 * @param {Column[] | CaptionedColumn[]} columns The columns being drawn.
 * @param {string | undefined} prefix Namespace the captions are keyed under.
 * @param {CaptionReader} read Reads one key.
 * @returns {Map<string, string>} The caption for each field; empty without a namespace.
 */
export const buildCaptions = (
  columns: CaptionedColumn[] | Column[],
  prefix: string | undefined,
  read: CaptionReader,
): Map<string, string> => {
  const captions = new Map<string, string>();
  if (!prefix) {
    return captions;
  }

  for (const column of columns as CaptionedColumn[]) {
    if (column.field) {
      captions.set(String(column.field), resolveCaption(column, prefix, read));
    }
  }

  return captions;
};

/**
 * Writes every caption to a live grid in one batch, so it repaints once.
 * @param {CaptionTarget | undefined} grid The live widget; nothing happens without one.
 * @param {Column[] | CaptionedColumn[]} columns The columns being drawn.
 * @param {string | undefined} prefix Namespace the captions are keyed under.
 * @param {CaptionReader} read Reads one key.
 */
export const applyCaptions = (
  grid: CaptionTarget | undefined,
  columns: CaptionedColumn[] | Column[],
  prefix: string | undefined,
  read: CaptionReader,
): void => {
  if (!prefix || !grid) {
    return;
  }

  grid.beginUpdate();
  try {
    for (const column of columns as CaptionedColumn[]) {
      if (column.field) {
        grid.columnOption(String(column.field), 'caption', resolveCaption(column, prefix, read));
      }
    }
  } finally {
    grid.endUpdate();
  }
};
